import React, { FC, memo, useMemo, useState } from 'react';
import { InterestedBoxProps } from '.';
import { HandledComponentError } from '@components/ErrorHandling';
import { ContinueCTAButton } from '@components/ContinueCTAButton';
import { SaveToYourProject } from 'src/components/SaveToCTA/SaveToYourProject';
import { useInterestedBoxLogic } from '@hooks/useInterestedBoxLogic';
import { useFeatureSwitch } from '@hooks/useFeatureSwitch';
import useTranslations from '@hooks/useTranslations';
import { FEATURE_DEMAND_TYPE, ModalVersion } from 'src/constants/main';
import { useDispatch } from 'react-redux';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { useJourneyType } from '@hooks/useJourneyType';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import Checkbox from '@components/Checkbox';
import { Pages } from 'src/types/pages';
import { useDealerDuck } from '@hooks/useDealerDuck';
import NeedHandSection from '@components/OfferListItem/NeedHandSection';
import { usePersonalizedOffer } from '@hooks/usePersonalizedOffer';
import FeatureDemandBanner from '@components/FeatureDemandBanner';
import { ITrimAttribute } from '../../../interfaces/Car';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useRouter } from 'next/router';
import routes from '../../../constants/routes';

export const InterestedBoxTemplate: FC<InterestedBoxProps> = memo(
    ({ className, page, isLegalText, onClickCta, onClickRequestMore, hideContinueButton = false }) => {
        const { showSaveButton } = useInterestedBoxLogic({ page });
        const router = useRouter();
        const {
            isReinsuranceInfoEnabled,
            isStockDealerShareConfigurationEnabled,
            isMTODealerShareConfigurationEnabled,
        } = useFeatureSwitch();
        const { isStockJourney } = useCarStockJourneyCheck();
        const { dealer } = useDealerDuck();
        const { carDetails } = useCarDetailsDuck();
        const { isPersonalizedDeal } = usePersonalizedOffer();
        const { isFinance, isRent } = useJourneyType();
        const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();
        const isBasketLegalTermsEnabled = useFeatureSwitchEnabled(
            FEATURES_LIST.FEATURE_SWITCH_BASKET_LEGAL_TERMS_AND_CONDITIONS
        );
        const [checkedA, setCheckedA] = useState<boolean>(false);
        const [checkedB, setCheckedB] = useState<boolean>(false);
        const { t } = useTranslations();
        const dispatch = useDispatch();

        const showBasketLegalTerms = isBasketLegalTermsEnabled && isFinance;

        const canContinue =
            (page === Pages.dealer && !dealer?.externalId) || (showBasketLegalTerms && (!checkedA || !checkedB));

        const checkBoxes = useMemo(
            () => [
                {
                    key: 1,
                    onChange: () => setCheckedA((prevState) => !prevState),
                    label: getNotEscapedTranslationMarkup(`checkout.legalTermsConds.consent1`),
                    checked: checkedA,
                },
                {
                    key: 2,
                    onChange: () => setCheckedB((prevState) => !prevState),
                    label: getNotEscapedTranslationMarkup(`checkout.legalTermsConds.consent2`),
                    checked: checkedB,
                },
            ],
            [checkedA, checkedB]
        );

        const featuresDemandPromoData = carDetails?.trimAttributes?.filter(
            (attr: ITrimAttribute) => attr.type === FEATURE_DEMAND_TYPE && attr.attributeCode === 'promo-block'
        );

        try {
            return (
                <div className={className}>
                    {showBasketLegalTerms && (
                        <div className="legals">
                            {checkBoxes.map((ch) => (
                                <div key={`ch${ch.key}`} className="checkbox-wrapper">
                                    <Checkbox
                                        className="checkbox-input"
                                        name={`legal_consent_checkbox_${ch.key}`}
                                        dataTestId={`TESTING_CONFIRM_LEGAL_TERMS_CHECKBOX_${ch.key}`}
                                        checked={ch.checked}
                                        onChange={ch.onChange}
                                    >
                                        <div className={'checkbox__text'} dangerouslySetInnerHTML={ch.label} />
                                    </Checkbox>
                                </div>
                            ))}
                        </div>
                    )}
                    {!isRent && (
                        <div className="btn-section">
                            {showSaveButton && !isPersonalizedDeal && <SaveToYourProject page={page} />}
                            {!hideContinueButton && (
                                <ContinueCTAButton
                                    disabled={canContinue}
                                    page={page}
                                    onClick={() => {
                                        onClickCta?.();
                                        onClickRequestMore?.();
                                    }}
                                    fullWidth
                                    dataId="TESTING_TO_BASKET_BOX_INTERESTEDBOX"
                                    htmlId="TESTING_TO_BASKET_BOX_INTERESTEDBOX"
                                    position="right-sidebar"
                                />
                            )}
                        </div>
                    )}
                    {[Pages.basket, Pages.basketV2].includes(page) && isReinsuranceInfoEnabled && !isRent && (
                        <button className="reinsurance">{t('continue.cta.reinsurance.text')}</button>
                    )}
                    {featuresDemandPromoData?.length > 0 &&
                        (router?.pathname?.includes(routes.CAR) || router?.pathname?.includes(routes.BASKET)) &&
                        featuresDemandPromoData?.map((featureDemandPromoData: ITrimAttribute, key: number) => (
                            <FeatureDemandBanner
                                key={key}
                                image={featureDemandPromoData.imageURL}
                                label={featureDemandPromoData.title}
                                title={featureDemandPromoData.description}
                                isConfigPage={router?.pathname?.includes(routes.CAR)}
                                isBasketPage={router?.pathname?.includes(routes.BASKET)}
                            />
                        ))}
                    <div className="links-section">
                        <NeedHandSection
                            isSidebar
                            isBasket={[Pages.basket, Pages.basketV2, Pages.dealer].includes(page)}
                        />
                        {isLegalText && (
                            <p className="legal-text" data-testid="TESTING_LEGAL_TEXT">
                                <span dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('filters.legal.text')} />
                            </p>
                        )}
                    </div>
                    {[Pages.basket, Pages.basketV2].includes(page) &&
                        ((isStockJourney && isStockDealerShareConfigurationEnabled) ||
                            (!isStockJourney && isMTODealerShareConfigurationEnabled)) && (
                            <button
                                className="dealerShare"
                                onClick={() =>
                                    dispatch(
                                        UIDuck.openModal({
                                            data: {},
                                            modalType: UIDuck.MODAL_TYPES.SHARE_CONFIGURATION_DEALER_CODE,
                                            modalVersion: ModalVersion.v2,
                                        })
                                    )
                                }
                            >
                                {t('shareConfiguration.dealerShareFeature')}
                            </button>
                        )}
                </div>
            );
        } catch (e: any) {
            return <HandledComponentError error={e} />;
        }
    }
);

InterestedBoxTemplate.displayName = 'InterestedBoxTemplate';
export default InterestedBoxTemplate;
